import moment from 'moment-timezone';
import { ScoreFactorStatus } from '../score-factor-detail-item/types';
import {
  SpfScore,
  CustomDomainScore,
  DailySendingQuotaScore,
  DmarcScore,
  EmailAgeScore,
  SendingIntervalScore,
  DkimScore,
  DomainBlacklistScore,
  IPBlacklistScore,
  MXRecordsScore,
} from './enums';
import { isPtrStatusNotValid } from '../../../components/email-accounts/enums/email-account';
import { EmailAccountSetup, EmailAccountSetupScore } from './types';

export const getLastCheckedDate = (
  emailAccountSetup: EmailAccountSetup,
  userTimezone: string,
): string => {
  if (emailAccountSetup && userTimezone) {
    const momentObj = moment(emailAccountSetup?.timeStamp || new Date());
    const date = momentObj.format('MMM DD');
    const year = momentObj.format('YYYY');
    const time = momentObj.format('hh:mm a').toUpperCase();
    const timezone = `GMT${momentObj.tz(userTimezone).format('Z')}`;

    return `${date}, ${year}. at ${time} ${timezone}`;
  }
  return '';
};

export const getScoreFactorStatusForSPF = (
  score: number,
): ScoreFactorStatus => {
  if (score === SpfScore.min) {
    return ScoreFactorStatus.NotValid;
  }
  return ScoreFactorStatus.Valid;
};

export const getScoreFactorStatusForCTD = (
  score: number,
): ScoreFactorStatus => {
  if (score === CustomDomainScore.min) {
    return ScoreFactorStatus.NotValid;
  }
  return ScoreFactorStatus.Valid;
};

export const getScoreFactorStatusForDSQ = (
  score: number,
): ScoreFactorStatus => {
  if (score) {
    if (score >= DailySendingQuotaScore.max) {
      return ScoreFactorStatus.Valid;
    }
  }
  return ScoreFactorStatus.PartiallyValid;
};

export const getScoreFactorStatusForDMARC = (
  score: number,
): ScoreFactorStatus => {
  if (score === DmarcScore.min) {
    return ScoreFactorStatus.NotValid;
  }
  return ScoreFactorStatus.Valid;
};

export const getScoreFactorStatusForDKIM = (
  score: number,
): ScoreFactorStatus => {
  if (score && score !== DkimScore.min) {
    return ScoreFactorStatus.Valid;
  }
  return ScoreFactorStatus.NotValid;
};

export const getScoreFactorStatusForPTR = (
  ptr: EmailAccountSetup['ptr'],
): ScoreFactorStatus => {
  if (!ptr) {
    return ScoreFactorStatus.NotValid;
  }
  if (isPtrStatusNotValid(ptr.status)) {
    return ScoreFactorStatus.NotValid;
  }
  return ScoreFactorStatus.Valid;
};

export const getScoreFactorStatusForDomainBlacklist = (
  factor: EmailAccountSetup['domainBlacklist'],
): ScoreFactorStatus => {
  if (factor?.inProgress) {
    return ScoreFactorStatus.InProgress;
  }
  if (factor?.isFreeDomainOrIp) {
    return ScoreFactorStatus.Valid;
  }
  if (
    factor?.score === DomainBlacklistScore.min &&
    factor?.data?.length === 0
  ) {
    return ScoreFactorStatus.NotFound;
  }
  if (factor?.score) {
    if (factor?.score === DomainBlacklistScore.max) {
      return ScoreFactorStatus.Valid;
    }
    if (
      factor?.score < DomainBlacklistScore.max &&
      factor?.score > DomainBlacklistScore.mid
    ) {
      return ScoreFactorStatus.PartiallyValid;
    }
  }
  return ScoreFactorStatus.NotValid;
};

export const getScoreFactorStatusForIPBlacklist = (
  factor: EmailAccountSetup['ipBlacklist'],
): ScoreFactorStatus => {
  if (factor?.inProgress) {
    return ScoreFactorStatus.InProgress;
  }
  if (factor?.isFreeDomainOrIp) {
    return ScoreFactorStatus.Valid;
  }
  if (factor?.score === IPBlacklistScore.min && factor?.data?.length === 0) {
    return ScoreFactorStatus.NotFound;
  }
  if (factor?.score) {
    if (factor?.score === IPBlacklistScore.max) {
      return ScoreFactorStatus.Valid;
    }
    if (
      factor?.score < IPBlacklistScore.max &&
      factor?.score > IPBlacklistScore.mid
    ) {
      return ScoreFactorStatus.PartiallyValid;
    }
  }
  return ScoreFactorStatus.NotValid;
};

export const getScoreFactorStatusForMXRecords = (
  score: number,
): ScoreFactorStatus => {
  if (score && score <= MXRecordsScore.max && score > MXRecordsScore.min) {
    return ScoreFactorStatus.Valid;
  }
  return ScoreFactorStatus.NotValid;
};

export const getScoreFactorStatusForEmailAge = (
  score: number,
): ScoreFactorStatus => {
  if (score) {
    if (score >= EmailAgeScore.max) {
      return ScoreFactorStatus.Valid;
    }
  }
  return ScoreFactorStatus.PartiallyValid;
};

export const getScoreFactorStatusForSendingInterval = (
  score: number,
): ScoreFactorStatus => {
  if (score) {
    if (score >= SendingIntervalScore.max) {
      return ScoreFactorStatus.Valid;
    }
  }
  return ScoreFactorStatus.PartiallyValid;
};

export const getEmailAccountSetupProgress = (
  emailAccountSetup: EmailAccountSetup,
) => {
  if (emailAccountSetup !== null) {
    if (emailAccountSetup?.total?.score <= 50) {
      return {
        scoreStatus: 'poor',
        pathColor: '#DC2626',
        trailColor: '#FEE2E2',
        textColor: '#DC2626',
      };
    }

    if (
      emailAccountSetup?.total?.score > 50 &&
      emailAccountSetup?.total?.score <= 70
    ) {
      return {
        scoreStatus: 'average',
        pathColor: '#F59E0B',
        trailColor: '#FEF3C7',
        textColor: '#F59E0B',
      };
    }

    if (
      emailAccountSetup?.total?.score > 70 &&
      emailAccountSetup?.total?.score <= 100
    ) {
      return {
        scoreStatus: 'excellent',
        pathColor: '#2DB482',
        trailColor: '#D1FAE5',
        textColor: '#2DB482',
      };
    }
  }
  return {
    scoreStatus: 'default',
    pathColor: '#ddd',
    trailColor: '#ddd',
    textColor: '#ddd',
  };
};

export const getEmailAccountHealthProgress = (
  emailAccountSetup: EmailAccountSetupScore,
) => {
  if (emailAccountSetup !== null) {
    if (emailAccountSetup?.total?.score <= 50) {
      return {
        scoreStatus: 'poor',
        pathColor: '#DC2626',
        trailColor: '#FEE2E2',
        textColor: '#DC2626',
      };
    }

    if (
      emailAccountSetup?.total?.score > 50 &&
      emailAccountSetup?.total?.score <= 70
    ) {
      return {
        scoreStatus: 'average',
        pathColor: '#F59E0B',
        trailColor: '#FEF3C7',
        textColor: '#F59E0B',
      };
    }

    if (
      emailAccountSetup?.total?.score > 70 &&
      emailAccountSetup?.total?.score <= 100
    ) {
      return {
        scoreStatus: 'excellent',
        pathColor: '#2DB482',
        trailColor: '#D1FAE5',
        textColor: '#2DB482',
      };
    }
  }
  return {
    scoreStatus: 'default',
    pathColor: '#ddd',
    trailColor: '#ddd',
    textColor: '#ddd',
  };
};

export const getEmailAgeDropdownSelectedKey = (score: number): string => {
  if (score === EmailAgeScore.min) {
    return '0';
  }
  if (score === EmailAgeScore.mid) {
    return '1';
  }
  return '2';
};

export const VALID_BLACKLIST_URLS: Record<string, string> = {
  'b.barracudacentral.org':
    'https://www.barracudacentral.org/rbl/removal-request',
  'zen.spamhaus.org': 'https://check.spamhaus.org/',
  'bl.spamcop.net': 'https://www.spamcop.net/bl.shtml',
  'spam.spamrats.com': 'https://www.spamrats.com/removal.php',
  'cbl.abuseat.org': 'https://check.spamhaus.org/',
  'dnsbl-0.uceprotect.net': 'https://www.uceprotect.net/en/index.php?m=7&s=0',
  'dnsbl-1.uceprotect.net':
    'https://www.dnsbl.info/dnsbl-details.php?dnsbl=dnsbl-1.uceprotect.net',
  'dnsbl-2.uceprotect.net':
    'https://www.dnsbl.info/dnsbl-details.php?dnsbl=dnsbl-2.uceprotect.net',
  'dnsbl-3.uceprotect.net':
    'https://www.dnsbl.info/dnsbl-details.php?dnsbl=dnsbl-3.uceprotect.net',
  'psbl.surriel.com': 'https://psbl.org/remove',
  'hostkarma.junkemailfilter.com':
    'https://ipadmin.junkemailfilter.com/remove.php',
  'black.junkemailfilter.com': 'https://ipadmin.junkemailfilter.com/remove.php',
  'dnsbl.cyberlogic.net':
    'https://www.dnsbl.info/dnsbl-details.php?dnsbl=dnsbl.cyberlogic.net ',
  'dnsbl.spfbl.net': 'https://spfbl.net/en/delist/',
  'multi.surbl.org': 'https://www.surbl.org/surbl-analysis',
  'dbl.spamhaus.org': 'https://check.spamhaus.org/',
  'z.mailspike.net': 'https://mailspike.io/ip_verify',
  'bl.mailspike.net': 'https://mailspike.io/ip_verify',
  'all.s5h.net':
    'https://www.usenix.org.uk/content/rbl.html#what-is-held-on-alls5hnet',
  'dnsbl.dronebl.org': 'https://dronebl.org/lookup',
  'badconf.rbl.schulte.org': 'https://rbl.schulte.org/',
  'spamguard.leadmon.net': 'mailto:SpamGuard@leadmon.net',
  'short.rbl.jp': '',
  'virus.rbl.jp': '',
  'fresh.spameatingmonkey.net': 'https://spameatingmonkey.com/lookup',
  'dnsbl.justspam.org': 'http://www.justspam.org/check-an-ip',
  'ipv4.bl.mailspike.net': 'https://mailspike.io/ip_verify',
  'query.senderbase.org': '',
  'dnsbl.tornevall.org': 'https://www.tornevall.net/removal/',
  'dyna.spamrats.com': 'https://www.spamrats.com/removal.php',
  'noptr.spamrats.com': 'https://www.spamrats.com/removal.php',
  'blackholes.five-ten-sg.com': '',
  'dnsbl.kempt.net': 'http://www.kempt.net/dnsbl/',
  'ubl.unsubscore.com': 'https://blacklist.lashback.com/',
  'rbl2.triumf.ca': 'mailto:postmaster@triumf.ca',
  'mail-abuse.blacklist.jippg.org': 'http://blacklist.jippg.org/',
  'blacklist.sci.kun.nl': '',
  'rbl.schulte.org': 'https://rbl.schulte.org/',
  'dyn.nszones.com': 'https://www.nszones.com/',
  'blackholes.mail-abuse.org': '',
};

export const getValidBlackistURLs = (
  blacklistData: { status: string; blacklist: string[] }[],
) => {
  const noURLsList = [];
  const validURLsList =
    blacklistData.flatMap(({ status, blacklist }) =>
      blacklist
        .filter((domain) => {
          if (domain in VALID_BLACKLIST_URLS) {
            if (VALID_BLACKLIST_URLS[domain] !== '') {
              return true;
            }
            noURLsList.push({
              status,
              blacklist: domain,
            });
            return false;
          }
          return false;
        })
        .map((domain) => ({
          status,
          blacklist: domain,
        })),
    ) || [];

  validURLsList.push(...noURLsList);

  return validURLsList;
};

export const getBlacklistedCount = (
  blacklistData: { status: string; blacklist: string[] }[],
) => {
  const filteredBlacklistData = getValidBlackistURLs(blacklistData);
  const blacklistedRecord = filteredBlacklistData?.filter(
    (item) => item.status === 'listed',
  );
  return blacklistedRecord?.length;
};
